import React, { useCallback, useEffect, useState } from 'react';

const STATUS_LABELS = { new: '새 문의', read: '읽음', done: '처리됨' };

function formatDate(s) {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all'); // all | new | read | done
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `?status=${filter}`;
      const res = await fetch(`/api/admin/contacts${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setItems(data.items || []);
        setCounts(data.counts || {});
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('contacts load:', e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const selected = items.find((i) => i.id === selectedId) || null;

  const handleSelect = async (item) => {
    setSelectedId(item.id);
    if (item.status === 'new') {
      // 클릭 시 자동으로 읽음 표시
      try {
        await fetch(`/api/admin/contacts/${item.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: 'read' }),
        });
        setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: 'read' } : p)));
        setCounts((prev) => ({
          ...prev,
          new: Math.max((prev.new || 0) - 1, 0),
          read: (prev.read || 0) + 1,
        }));
      } catch (_) { /* ignore */ }
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`/api/admin/contacts/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) load();
    } catch (e) { console.error(e); }
  };

  const removeItem = async (id) => {
    if (!window.confirm('이 문의를 삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`/api/admin/contacts/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.success) {
        setItems((prev) => prev.filter((p) => p.id !== id));
        if (selectedId === id) setSelectedId(null);
        load();
      }
    } catch (e) { console.error(e); }
  };

  const filterCount = (key) => {
    if (key === 'all') return total;
    return counts[key] || 0;
  };

  return (
    <>
      <header className="admin-page-header">
        <div>
          <h1 className="admin-page-title">문의 관리</h1>
          <p className="admin-page-desc">Contact 페이지에서 들어온 문의를 확인하고 처리합니다.</p>
        </div>
        <div className="admin-filter-bar">
          {['all', 'new', 'read', 'done'].map((k) => (
            <button
              key={k}
              type="button"
              className={'admin-filter-pill' + (filter === k ? ' active' : '')}
              onClick={() => { setFilter(k); setSelectedId(null); }}
            >
              {k === 'all' ? '전체' : STATUS_LABELS[k]}
              <span className="count">{filterCount(k)}</span>
            </button>
          ))}
        </div>
      </header>

      <div className={'admin-contacts' + (selected ? '' : ' no-detail')}>
        <div className="admin-card">
          {loading ? (
            <div className="admin-empty">불러오는 중...</div>
          ) : items.length === 0 ? (
            <div className="admin-empty">표시할 문의가 없습니다.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 92 }}>상태</th>
                  <th style={{ width: 120 }}>이름</th>
                  <th style={{ width: 200 }}>이메일</th>
                  <th>메시지</th>
                  <th style={{ width: 140 }}>받은 시각</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className={(it.status === 'new' ? 'unread ' : '') + (it.id === selectedId ? 'selected' : '')}
                    onClick={() => handleSelect(it)}
                  >
                    <td><span className={`admin-status ${it.status}`}>{STATUS_LABELS[it.status] || it.status}</span></td>
                    <td>{it.user_name}</td>
                    <td>{it.user_email}</td>
                    <td className="admin-cell-msg">{it.message}</td>
                    <td>{formatDate(it.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="admin-card admin-detail">
            <div className="admin-detail-row">
              <span className="key">이름</span>
              <span className="val">{selected.user_name}</span>
            </div>
            <div className="admin-detail-row">
              <span className="key">이메일</span>
              <span className="val">{selected.user_email}</span>
            </div>
            <div className="admin-detail-row">
              <span className="key">상태</span>
              <span className="val">
                <span className={`admin-status ${selected.status}`}>{STATUS_LABELS[selected.status] || selected.status}</span>
              </span>
            </div>
            <div className="admin-detail-row">
              <span className="key">받은 시각</span>
              <span className="val">{formatDate(selected.created_at)}</span>
            </div>
            <div className="admin-detail-msg">{selected.message}</div>
            <div className="admin-detail-actions">
              <a
                className="admin-action-btn primary"
                href={`mailto:${selected.user_email}?subject=${encodeURIComponent('[AntsNest] 문의 답변')}&body=${encodeURIComponent(`안녕하세요 ${selected.user_name}님,\n\n문의 주신 내용에 답변드립니다.\n\n— 원본 문의 —\n${selected.message}\n`)}`}
              >
                메일로 답장
              </a>
              {selected.status !== 'done' && (
                <button type="button" className="admin-action-btn success" onClick={() => updateStatus(selected.id, 'done')}>처리됨으로 표시</button>
              )}
              {selected.status === 'done' && (
                <button type="button" className="admin-action-btn" onClick={() => updateStatus(selected.id, 'read')}>다시 열기</button>
              )}
              <button type="button" className="admin-action-btn danger" onClick={() => removeItem(selected.id)}>삭제</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
